import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import CircularProgress from '@material-ui/core/CircularProgress';

import {helper, http, api, defer, deferToken} from '../../common/common';

const styles = (theme) => {
    return {
        view: {
            position: 'absolute',
            backgroundColor: 'rgba(255,255,255,0.97)',
            textAlign: 'center',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
        },
        container: {
            width: 594,
            marginTop: 24,
            maringBottom: 24,
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        headingText: {
            color: theme.palette.text.primary,
        },
        subheadingText: {
            marginTop: 16,
            marginBottom: 0,
            fontWeight: 500,
            color: theme.palette.text.primary,
        },
        contentPanel: {
            paddingLeft: 8,
            paddingRight: 8,
        },
        scrollablePanel: {
            minHeight: '10%',
            maxHeight: '50%',
            overflow: 'auto',
        },
        contentRow: {
            height: 36,
        },
        contentText: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            borderBottomStyle: 'none',
        },
        footerText: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 500,
            borderTop: '1px solid',
            borderTopColor: theme.palette.divider,
            borderBottomStyle: 'none',
        },
        firstCell: {
            width: 140,
            paddingLeft: 16,
        },
        controlGroup: {
            width: '100%',
            height: 36,
            paddingTop: 16,
            paddingBottom: 16,
            textAlign: 'right',
        },
        control: {
            float: 'right',
            marginRight: 8,
            marginLeft: 8,
        },
        spinnerPlaceholder: {
            position: 'absolute',
            top: 'calc(50% - 60px)',
            left: 'calc(50% - 80px)',
            width: 160,
            height: 120,
            textAlign: 'center',
        },
        spinner: {
            color: theme.palette.text.disabled,
        },
    };
}

class SelectionConfirm extends Component {
    constructor(props) {
        super(props);

        this._totalCost = 0;

        this.state = {
            pending: true,
        };

        this._cancelToken = http.CancelToken.source();
        this._timeoutToken = null;
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        return (this.props.open !== nextProps.open || this.state.pending !== nextState.pending);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (!prevProps.open && this.props.open) {
            this.setState({
                pending: true,
            });

            this._requestCheckDuplicates(this.props);
        }
    }

    componentWillUnmount = () => {
        if (this._timeoutToken) {
            this._timeoutToken.cancel();
        }

        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        if (!this.props.open) {
            return null;
        }

        const {classes} = this.props;

        if (!this.props.open) {
            return null;
        }

        return (
            <div className={classes.view}>
                {(this.state.pending) ? this._renderSpinner() : this._renderContent()}
            </div>
        );
    }

    _renderSpinner = () => {
        const {classes} = this.props;

        if (!this._timeoutToken) {
            this._timeoutToken = deferToken.source();
            defer(15000, this._timeoutToken).then(this._notifyLoadingFailed);
        }

        return (
            <div className={classes.spinnerPlaceholder}>
                <CircularProgress
                    className={classes.spinner}
                    size={60}
                />
            </div>
        );
    }

    _renderContent = () => {
        if (this._timeoutToken) {
            this._timeoutToken.cancel();
            this._timeoutToken = null;
        }

        if (!this._regionInfo) {
            this._notifyMissingRegionContent();
            return (<div/>);
        }

        if (this._selectedAnalysis.length === 0) {
            this._notifyMissingAnalysisContent();
            return (<div/>);
        }

        const {classes} = this.props;

        return (
            <div className={classes.container}>
                <Typography
                    className={classes.headingText}
                    align='center'
                    variant='h4'
                >
                    Confirm Analysis
                </Typography>
                {this._renderHeader()}
                {this._renderBody()}
                <div className={classes.controlGroup}>
                    <Button
                        className={classes.control}
                        color='secondary'
                        autoFocus={true}
                        disabled={!this._regionInfo.size || !this._totalCost}
                        variant='contained'
                        onClick={this._handleContinue}
                    >
                        Confirm
                    </Button>
                    <Button
                        className={classes.control}
                        color='secondary'
                        onClick={this._handleCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        );
    }

    _renderHeader = () => {
        const {classes} = this.props;

        var size = 'Unknown';

        if (this._regionInfo.size) {
            size = this._regionInfo.size.toLocaleString('en');
        }

        return (
            <div className={classes.contentPanel}>
                <Typography
                    className={classes.subheadingText}
                    align='left'
                    variant='h6'
                >
                    Information
                </Typography>
                <Table>
                    <TableBody>
                        <TableRow className={classes.contentRow}>
                            <TableCell
                                className={classNames(classes.contentText, classes.firstCell)}
                                padding='none'
                            >
                                Region
                            </TableCell>
                            <TableCell
                                className={classes.contentText}
                                padding='none'
                            >
                                {this._regionInfo.name} ({size} Ha)
                            </TableCell>
                        </TableRow>
                        <TableRow className={classes.contentRow}>
                            <TableCell
                                className={classNames(classes.contentText, classes.firstCell)}
                                padding='none'
                            >
                                Date
                            </TableCell>
                            <TableCell
                                className={classes.contentText}
                                padding='none'
                            >
                                {helper.makeDate(this.props.overlayInfo.date).toDateString()}
                            </TableCell>
                        </TableRow>
                        <TableRow className={classes.contentRow}>
                            <TableCell
                                className={classNames(classes.contentText, classes.firstCell)}
                                padding='none'
                            >
                                Image
                            </TableCell>
                            <TableCell
                                className={classes.contentText}
                                padding='none'
                            >
                                {this._stringifyCategory(this.props.overlayInfo.category)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        );
    }

    _renderBody = () => {
        const {classes} = this.props;

        this._totalCost = 0;

        return (
            <div className={classes.contentPanel}>
                <Typography
                    className={classes.subheadingText}
                    align='left'
                    variant='h6'
                >
                    Selected Analysis
                </Typography>
                <div className={classes.scrollablePanel}>
                    <Table>
                        <TableBody>
                            {this._selectedAnalysis.map(this._renderLineItem)}
                            <TableRow className={classes.contentRow}>
                                <TableCell
                                    className={classNames(classes.footerText, classes.firstCell)}
                                    padding='none'
                                >
                                    Total
                                </TableCell>
                                <TableCell
                                    className={classes.footerText}
                                    padding='none'
                                />
                                <TableCell
                                    className={classes.footerText}
                                    padding='none'
                                    numeric={true}
                                >
                                    {this._totalCost.toLocaleString('en')} USD
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        );
    }

    _renderLineItem = (data, index, arr) => {
        const {classes} = this.props;

        const size = (this._regionInfo.size) ? this._regionInfo.size : 0;

        var cost = (data.unit_cost * size).toLocaleString('en') + ' USD';

        if (!data.exists) {
            this._totalCost += (data.unit_cost * size);
        } else {
            cost = 'Already Exist'
        }

        return (
            <TableRow key={data.key} className={classes.contentRow}>
                <TableCell
                    className={classNames(classes.contentText, classes.firstCell)}
                    padding='none'
                >
                    {data.label}
                </TableCell>
                <TableCell
                    className={classes.contentText}
                    padding='none'
                    numeric={false}
                >
                    {data.unit_cost.toLocaleString('en')} USD/Ha
                </TableCell>
                <TableCell
                    className={classes.contentText}
                    padding='none'
                    numeric={true}
                >
                    {cost}
                </TableCell>
            </TableRow>
        );
    }

    _handleContinue = () => {
        this._requestTasking();
    }

    _handleCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    _stringifyCategory = (category) => {
        switch (category) {
            case 'hi_res':
                return 'High resolution';
            case 'mid_res':
                return 'Middle resolution';
            case 'low_res':
                return 'Low resolution';
            default:
                return 'Unknown';
        }
    }

    _toAnalysisEntry = (data) => {
        return data.key;
    }

    _fromAnalysisEntry = (data) => {
        return {
            key: data.key,
            label: data.label,
            unit_cost: data.unit_cost * 0.01,
            exists: data.exists,
        };
    }

    _toEntryKey = (data) => {
        return data.key;
    }

    _processError = (error) => {
        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    _notifyLoadingFailed = () => {
        this._processError(helper.makeError('Error',
            'Unable to connect to tasking server', 'warn_only'));

        this._handleCancel();
    }

    _notifyMissingRegionContent = async () => {
        this._processError(helper.makeError('Error',
            'No region was selected', 'warn_only'));

        this._handleCancel();
    }

    _notifyMissingAnalysisContent = async () => {
        this._processError(helper.makeError('Error',
            'No valid analysis was request', 'warn_only'));

        this._handleCancel();
    }

    _requestCheckDuplicates = async ({regionInfo, overlayInfo, selectedAnalysis}) => {
        try {
            const response = await http.post(api.tasking() + '/check', {
                'access_code': this.props.getAccessCode(),
                'organization': regionInfo.organization,
                'region_key': regionInfo.key,
                'mode': overlayInfo.mode,
                'date': overlayInfo.date,
                'analysis_key': selectedAnalysis.map(this._toAnalysisEntry),
            }, {
                cancelToken: this._cancelToken.token,
            });

            this._regionInfo = regionInfo;
            this._selectedAnalysis = selectedAnalysis;
            this._regionInfo.size = response.data.size;
            this._selectedAnalysis = response.data.analysis.map(this._fromAnalysisEntry);

            this.setState({
                pending: false,
            });
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._handleCancel();

            const e = helper.sanitizeError(error, 'Error', 'Fail to check tasking', 'warn_only');
            this._processError(e);
        }
    }

    _requestTasking = async () => {
        try {
            this.setState({
                pending: true,
            });

            const {overlayInfo} = this.props;
            const regionInfo = this._regionInfo;
            const selectedAnalysis = this._selectedAnalysis;

            await http.post(api.tasking() + '/new-job', {
                'access_code': this.props.getAccessCode(),
                'entry_key': overlayInfo.content.map(this._toEntryKey),
                'organization': regionInfo.organization,
                'region_key': regionInfo.key,
                'analysis_key': selectedAnalysis.map(this._toAnalysisEntry),
            }, {
                cancelToken: this._cancelToken.token,
            });

            this.setState({
                pending: false,
            });

            if (this.props.onNext) {
                this.props.onNext();
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._handleCancel();

            const e = helper.sanitizeError(error, 'Error', 'Fail to get new tasking', 'warn_only');
            this._processError(e);
        }
    }
}

SelectionConfirm.propTypes = {
    open: PropTypes.bool.isRequired,
    regionInfo: PropTypes.object,
    overlayInfo: PropTypes.object,
    selectedAnalysis: PropTypes.arrayOf(PropTypes.object),
    getAccessCode: PropTypes.func.isRequired,
    onNext: PropTypes.func,
    onCancel: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles)(SelectionConfirm);
